/**
 * Support Tickets Filter Configuration
 */

import { createFilterConfig, type FilterFieldConfig } from '@ufc/core'
import type { SupportTicketsFilter, TicketStatus, SupportSortBy, SupportSortOrder } from './types'
import { DEFAULT_PAGE_SIZE, DEFAULT_SUPPORT_SORT } from './constants'

export const TICKET_STATUS_OPTIONS: Array<{ value: TicketStatus; labelKey: string; color: string }> = [
  { value: 'open', labelKey: 'support.statusOpen', color: 'blue' },
  { value: 'in_progress', labelKey: 'support.statusInProgress', color: 'amber' },
  { value: 'waiting_on_reporter', labelKey: 'support.statusWaiting', color: 'purple' },
  { value: 'resolved', labelKey: 'support.statusResolved', color: 'green' },
  { value: 'closed', labelKey: 'support.statusClosed', color: 'gray' },
]

export const DEFAULT_SUPPORT_FILTER: SupportTicketsFilter = {
  search: undefined,
  status: [],
  channel: undefined,
  priority: undefined,
  category: undefined,
  sortBy: DEFAULT_SUPPORT_SORT.sortBy as SupportSortBy,
  sortOrder: DEFAULT_SUPPORT_SORT.sortOrder as SupportSortOrder,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
}

export const SUPPORT_FILTER_FIELDS: FilterFieldConfig[] = [
  {
    id: 'search',
    type: 'text',
    label: 'common.search',
    placeholder: 'support.searchPlaceholder',
    defaultValue: '',
    debounceMs: 300,
  },
  {
    id: 'status',
    type: 'multi-select',
    label: 'support.filterByStatus',
    placeholder: 'support.selectStatus',
    defaultValue: [],
    options: TICKET_STATUS_OPTIONS.map((opt) => ({
      value: opt.value,
      label: opt.labelKey,
      color: opt.color,
    })),
    showSelectedCount: true,
  },
]

function serializeSupportFilter(filter: SupportTicketsFilter): Record<string, string> {
  const params: Record<string, string> = {}

  if (filter.search) params.search = filter.search
  if (filter.status && filter.status.length > 0) params.status = filter.status.join(',')
  if (filter.channel) params.channel = filter.channel
  if (filter.priority) params.priority = filter.priority
  if (filter.category) params.category = filter.category
  if (filter.sortBy && filter.sortBy !== DEFAULT_SUPPORT_SORT.sortBy) params.sortBy = filter.sortBy
  if (filter.sortOrder && filter.sortOrder !== DEFAULT_SUPPORT_SORT.sortOrder) params.sortOrder = filter.sortOrder
  if (filter.page && filter.page > 1) params.page = String(filter.page)
  if (filter.pageSize && filter.pageSize !== DEFAULT_PAGE_SIZE) params.pageSize = String(filter.pageSize)

  return params
}

function deserializeSupportFilter(params: Record<string, string | null>): Partial<SupportTicketsFilter> {
  const filter: Partial<SupportTicketsFilter> = {}

  if (params.search) filter.search = params.search
  if (params.status) {
    const statuses = params.status.split(',').filter(Boolean) as TicketStatus[]
    if (statuses.length > 0) filter.status = statuses
  }
  if (params.channel) filter.channel = params.channel as any
  if (params.priority) filter.priority = params.priority as any
  if (params.category) filter.category = params.category as any
  if (params.sortBy) filter.sortBy = params.sortBy as SupportSortBy
  if (params.sortOrder && (params.sortOrder === 'asc' || params.sortOrder === 'desc')) {
    filter.sortOrder = params.sortOrder
  }
  if (params.page) {
    const page = Number(params.page)
    if (Number.isInteger(page) && page > 0) filter.page = page
  }
  if (params.pageSize) {
    const pageSize = Number(params.pageSize)
    if (Number.isInteger(pageSize) && pageSize > 0 && pageSize <= 100) filter.pageSize = pageSize
  }

  return filter
}

function validateSupportFilter(filter: Partial<SupportTicketsFilter>): SupportTicketsFilter {
  const validStatuses: TicketStatus[] = ['open', 'in_progress', 'waiting_on_reporter', 'resolved', 'closed']

  return {
    ...DEFAULT_SUPPORT_FILTER,
    ...filter,
    status: (filter.status || []).filter((s) => validStatuses.includes(s)),
    page: Math.max(1, filter.page ?? 1),
    pageSize: Math.min(100, Math.max(1, filter.pageSize ?? DEFAULT_PAGE_SIZE)),
  }
}

export const supportFilterConfig = createFilterConfig<SupportTicketsFilter>({
  domainName: 'support',
  fields: SUPPORT_FILTER_FIELDS,
  defaults: DEFAULT_SUPPORT_FILTER,
  serialize: serializeSupportFilter,
  deserialize: deserializeSupportFilter,
  validate: validateSupportFilter,
  paramMapping: {},
})

export interface SupportTicketsSearchParams {
  search?: string
  status?: string
  channel?: string
  priority?: string
  category?: string
  sortBy?: string
  sortOrder?: string
  page?: string
  pageSize?: string
}

export function validateSupportTicketsSearch(search: Record<string, unknown>): SupportTicketsSearchParams {
  return {
    search: typeof search.search === 'string' ? search.search : undefined,
    status: typeof search.status === 'string' ? search.status : undefined,
    channel: typeof search.channel === 'string' ? search.channel : undefined,
    priority: typeof search.priority === 'string' ? search.priority : undefined,
    category: typeof search.category === 'string' ? search.category : undefined,
    sortBy: typeof search.sortBy === 'string' ? search.sortBy : undefined,
    sortOrder: typeof search.sortOrder === 'string' ? search.sortOrder : undefined,
    page: typeof search.page === 'string' ? search.page : undefined,
    pageSize: typeof search.pageSize === 'string' ? search.pageSize : undefined,
  }
}
